import { useEffect, useState } from "react";
import { useParams, useNavigate, useSearchParams, Link } from "react-router-dom";
import Layout from "../components/Layout";
import AiOutputDisclaimer from "../components/AiOutputDisclaimer";
import { api } from "../lib/api";
import { renderMarkdown } from "../lib/markdown";
import { decoratePdfWithDisclaimer } from "../lib/pdf";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { ArrowLeft, Loader2, ShieldCheck, Clock, Download, Stamp } from "lucide-react";
import { jsPDF } from "jspdf";

const STATUS_TEXT = {
  pending_payment: "Awaiting payment",
  pending_review: "Awaiting specialist review — typically within 24 hours",
  reviewed: "Reviewed & Co-signed",
};

export default function ConsultationDetail() {
  const { id } = useParams();
  const [params, setParams] = useSearchParams();
  const reviewSessionId = params.get("review_session_id");
  const { user } = useAuth();
  const nav = useNavigate();
  const [cons, setCons] = useState(null);
  const [review, setReview] = useState(null);
  const [quota, setQuota] = useState(null);
  const [requesting, setRequesting] = useState(false);

  const load = async () => {
    try {
      const [c, q, r] = await Promise.all([
        api.get(`/consultations/${id}`),
        api.get(`/reviews/quota`),
        api.get(`/reviews`).then((res) => res.data.find((x) => x.consultation_id === id) || null),
      ]);
      setCons(c.data);
      setQuota(q.data);
      setReview(r);
    } catch (e) {
      toast.error("Failed to load consultation");
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Returning from Stripe checkout → poll status
  useEffect(() => {
    if (!reviewSessionId) return;
    let attempts = 0;
    const poll = async () => {
      attempts += 1;
      try {
        const r = await api.get(`/reviews/payment-status/${reviewSessionId}`);
        if (r.data.status !== "pending_payment") {
          setReview(r.data.review);
          toast.success("Payment received — case sent to Dr. Seecharan");
          // Strip the query param
          params.delete("review_session_id");
          setParams(params, { replace: true });
          return;
        }
      } catch (_) {}
      if (attempts < 8) setTimeout(poll, 2000);
    };
    poll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reviewSessionId]);

  const requestReview = async () => {
    setRequesting(true);
    try {
      const r = await api.post("/reviews/request", {
        consultation_id: id,
        origin_url: window.location.origin,
      });
      if (r.data.checkout_url) {
        window.location.href = r.data.checkout_url;
      } else {
        setReview(r.data.review);
        toast.success(
          r.data.free_used
            ? "Case sent to Dr. Seecharan (free with Practice plan)"
            : "Review requested"
        );
        load();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not request review");
    } finally {
      setRequesting(false);
    }
  };

  const exportPdf = () => {
    if (!cons) return;
    const doc = new jsPDF({ unit: "pt", format: "letter" });
    const margin = 54;
    const width = 612 - margin * 2;
    let y = margin;

    doc.setFont("times", "bold");
    doc.setFontSize(18);
    doc.text("ProsthoAI Consultation", margin, y);
    y += 22;
    doc.setFont("times", "normal");
    doc.setFontSize(11);
    doc.text(cons.module_name, margin, y);
    y += 14;
    doc.setFontSize(9);
    doc.setTextColor(100);
    doc.text(`Generated: ${new Date(cons.created_at).toLocaleString()}`, margin, y);
    if (cons.patient_name) {
      y += 12;
      doc.text(`Patient: ${cons.patient_name}`, margin, y);
    }
    if (user?.full_name) {
      y += 12;
      doc.text(`Prepared for: ${user.full_name}`, margin, y);
    }
    y += 18;
    doc.setDrawColor(212, 175, 55);
    doc.line(margin, y, 612 - margin, y);
    y += 16;

    // Inputs
    doc.setTextColor(40);
    doc.setFont("times", "bold");
    doc.setFontSize(12);
    doc.text("Clinical Inputs", margin, y);
    y += 14;
    doc.setFont("times", "normal");
    doc.setFontSize(10);
    Object.entries(cons.inputs || {}).forEach(([k, v]) => {
      const label = k.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());
      const line = doc.splitTextToSize(`• ${label}: ${v}`, width);
      if (y + line.length * 12 > 750) {
        doc.addPage();
        y = margin;
      }
      doc.text(line, margin, y);
      y += line.length * 12 + 2;
    });

    y += 10;
    doc.setFont("times", "bold");
    doc.setFontSize(12);
    doc.text("Specialist Analysis", margin, y);
    y += 14;
    doc.setFont("times", "normal");
    doc.setFontSize(10.5);
    const cleaned = (cons.response || "").replace(/[#*`]/g, "");
    doc.splitTextToSize(cleaned, width).forEach((ln) => {
      if (y > 740) {
        doc.addPage();
        y = margin;
      }
      doc.text(ln, margin, y);
      y += 13;
    });

    // Co-signature block
    if (review?.status === "reviewed") {
      if (y > 680) {
        doc.addPage();
        y = margin;
      }
      y += 28;
      doc.setDrawColor(212, 175, 55);
      doc.setLineWidth(1.5);
      doc.rect(margin, y - 12, width, 90);
      doc.setLineWidth(0.5);
      doc.setFont("times", "bold");
      doc.setFontSize(11);
      doc.setTextColor(212, 175, 55);
      doc.text("SPECIALIST CO-SIGNATURE", margin + 12, y + 4);
      y += 22;
      doc.setTextColor(40);
      doc.setFont("times", "normal");
      doc.setFontSize(10);
      const stamp = `Reviewed and approved by ${review.approved_by_name} — ${review.approved_by_title} — ${
        review.approved_at ? new Date(review.approved_at).toLocaleDateString() : ""
      }.`;
      doc.splitTextToSize(stamp, width - 24).forEach((ln) => {
        doc.text(ln, margin + 12, y);
        y += 13;
      });
      if (review.specialist_notes) {
        y += 4;
        doc.setFont("times", "italic");
        doc.setFontSize(9.5);
        doc.text("Specialist notes:", margin + 12, y);
        y += 12;
        doc.setFont("times", "normal");
        doc.splitTextToSize(review.specialist_notes, width - 24).forEach((ln) => {
          if (y > 740) {
            doc.addPage();
            y = margin;
          }
          doc.text(ln, margin + 12, y);
          y += 12;
        });
      }
    }

    // Footer
    if (y > 720) {
      doc.addPage();
      y = margin;
    }
    y += 20;
    doc.setFontSize(8);
    doc.setTextColor(120);
    doc.text(
      "Patent Pending US Application 64/062,147 · Dr. Ronik Seecharan DMD, Prosthodontist — 30 years of clinical practice dedicated to treatment planning excellence",
      margin,
      y
    );
    y += 10;
    doc.text(
      "Clinical decision support tool. Not a substitute for licensed professional judgment.",
      margin,
      y
    );

    // Mandatory disclaimer decoration (header strip + watermark + footer block)
    decoratePdfWithDisclaimer(doc);

    doc.save(`prosthoai-${cons.module_slug}-${cons.id.slice(0, 8)}.pdf`);
  };

  if (!cons)
    return (
      <Layout>
        <div className="text-[var(--pa-text-muted)] flex items-center gap-2">
          <Loader2 size={14} className="animate-spin" /> Loading…
        </div>
      </Layout>
    );

  const reviewed = review?.status === "reviewed";

  return (
    <Layout>
      <button
        onClick={() => nav(-1)}
        className="inline-flex items-center gap-1 text-sm text-[var(--pa-text-muted)] hover:text-[var(--pa-gold)] transition"
      >
        <ArrowLeft size={14} /> Back
      </button>

      <div className="mt-4 flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="kicker">{cons.module_name}</div>
          <h1
            className="font-serif text-3xl md:text-4xl text-[var(--pa-text)] mt-1"
            data-testid="consultation-title"
          >
            Consultation
          </h1>
          <div className="text-xs text-[var(--pa-text-dim)] mt-1">
            {cons.patient_name && <span>Patient: {cons.patient_name} · </span>}
            {new Date(cons.created_at).toLocaleString()}
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={exportPdf}
            data-testid="cons-export-pdf"
            className="flex items-center gap-1.5 border border-[var(--pa-gold)] text-[var(--pa-gold)] text-sm px-4 py-2 rounded-md hover:bg-[var(--pa-gold)]/10 transition"
          >
            <Download size={14} /> PDF{reviewed ? " (co-signed)" : ""}
          </button>
        </div>
      </div>

      <div className="gold-divider my-6" />

      {/* Specialist review banner */}
      <SpecialistReviewBanner
        review={review}
        quota={quota}
        requesting={requesting}
        onRequest={requestReview}
      />

      <AiOutputDisclaimer className="mt-6" />

      <div
        className="prose-clinical mt-6 bg-[var(--pa-surface)] border border-[var(--pa-border-active)] rounded-lg p-6"
        dangerouslySetInnerHTML={{ __html: renderMarkdown(cons.response) }}
      />
    </Layout>
  );
}

function SpecialistReviewBanner({ review, quota, requesting, onRequest }) {
  if (!review) {
    const isPractice = quota?.is_practice;
    const free = quota?.free_remaining || 0;
    return (
      <div
        data-testid="review-cta"
        className="bg-[var(--pa-surface)] border border-[var(--pa-gold)] rounded-lg p-5 flex flex-wrap items-center justify-between gap-4"
      >
        <div className="flex items-start gap-3">
          <ShieldCheck size={22} className="text-[var(--pa-gold)] mt-0.5" />
          <div>
          <div className="kicker">Specialist Review by Dr. Ronik Seecharan DMD, Prosthodontist — 30 years of clinical practice dedicated to treatment planning excellence</div>
            <div className="text-sm text-[var(--pa-text)] mt-1">
              Have this case reviewed and co-signed by Dr. Ronik Seecharan DMD,
              Prosthodontist — 30 years of clinical practice dedicated to treatment planning
              excellence.
            </div>
            <div className="text-xs text-[var(--pa-text-muted)] mt-1.5">
              {isPractice && free > 0
                ? `Free with your Practice plan (${free} of ${quota.free_total} remaining this month)`
                : `Tier 2 · $${quota?.price_per_review_usd || 150} per module review · typically within 24 hours`}
            </div>
          </div>
        </div>
        <button
          onClick={onRequest}
          disabled={requesting}
          data-testid="request-review"
          className="bg-[var(--pa-gold)] text-[var(--pa-bg)] font-semibold rounded-md px-5 py-2.5 hover:bg-[var(--pa-gold-hover)] transition disabled:opacity-50 flex items-center gap-2"
        >
          {requesting ? (
            <>
              <Loader2 size={14} className="animate-spin" /> Requesting…
            </>
          ) : isPractice && free > 0 ? (
            "Request review (free)"
          ) : (
            "Request specialist review · $150"
          )}
        </button>
      </div>
    );
  }
  if (review.status === "reviewed") {
    return (
      <div
        data-testid="review-stamp"
        className="bg-emerald-500/10 border border-emerald-500/40 rounded-lg p-5"
      >
        <div className="flex items-start gap-3">
          <Stamp size={22} className="text-emerald-400 mt-0.5" />
          <div className="flex-1">
            <div className="kicker text-emerald-400">Reviewed & Co-Signed</div>
            <div className="text-sm text-[var(--pa-text)] mt-1">
              Reviewed and approved by {review.approved_by_name} — {review.approved_by_title} —{" "}
              {review.approved_at ? new Date(review.approved_at).toLocaleDateString() : ""}.
            </div>
            {review.specialist_notes && (
              <div className="mt-3 p-3 bg-[var(--pa-bg-2)] border border-[var(--pa-border)] rounded text-xs text-[var(--pa-text-muted)] whitespace-pre-wrap">
                <span className="font-semibold text-[var(--pa-gold)]">Specialist notes: </span>
                {review.specialist_notes}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }
  // pending_payment / pending_review
  const text = STATUS_TEXT[review.status] || "In progress";
  return (
    <div
      data-testid="review-pending"
      className="bg-[var(--pa-surface)] border border-amber-400/40 rounded-lg p-5 flex items-center gap-3"
    >
      <Clock size={18} className="text-amber-400" />
      <div className="text-sm text-[var(--pa-text)]">{text}</div>
    </div>
  );
}
